// Form DINÂMICO do provider — renderiza os `fields[]` do /metadata
// por tipo (STRING / BOOLEAN / ENUM com options / SECRET e PEM_* como
// write-only "•••• definido" + botão "alterar", molde
// zenvia-credentials-card). Estado controlado (não RHF): os campos
// são dinâmicos por metadata, e o payload sai PARCIAL — só o que o
// usuário alterou; write-only não alterado FICA FORA (mascarado
// jamais é reenviado — daria 400 no ProviderFieldsValidator).
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PasswordInput } from "@/components/ui/password-input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import type {
  ProcessorConfigProviderValues,
  ProcessorMetadataField,
} from "@/types/admin-processor-config"

const panelPrefix = "pages.adminProcessorConfig"

// Tipos write-only (read = `<name>Defined`; nunca ecoados).
const WRITE_ONLY_TYPES = new Set([
  "SECRET",
  "PEM_PRIVATE_KEY",
  "PEM_CERTIFICATE",
])

// A referência ao cofre é editada na aba Certificado, junto do metadata X509 que
// dá contexto à escolha. Repetir aqui mostraria o uuid solto, sem o que ele
// significa.
export const isCertificateRefField = (
  field: ProcessorMetadataField
): boolean => field.type === "CERTIFICATE_REF"

export const isWriteOnlyField = (
  field: ProcessorMetadataField
): boolean =>
  field.writeOnly || WRITE_ONLY_TYPES.has(field.type)

// defaultValue do form por campo a partir do provider do detail.
const initialValue = (
  field: ProcessorMetadataField,
  current?: ProcessorConfigProviderValues
): string | boolean => {
  if (field.type === "BOOLEAN") {
    return Boolean(current?.[field.name])
  }
  // Write-only nunca vem no read — input nasce vazio (após "alterar").
  if (isWriteOnlyField(field)) return ""
  const value = current?.[field.name]
  return value === null || value === undefined
    ? ""
    : String(value)
}

type ProviderFormProps = {
  fields: ProcessorMetadataField[]
  // Valores atuais (detail) — ausente no modo criação.
  currentValues?: ProcessorConfigProviderValues
  mode: "edit" | "create"
  saving: boolean
  // Recebe SÓ os campos alterados (edit) ou preenchidos (create).
  onSubmit: (provider: Record<string, unknown>) => void
  // Ações extras à esquerda do footer (ex.: Testar conexão).
  footerStart?: React.ReactNode
  /**
   * Campos que pertencem ao mesmo salvamento mas não vêm do metadata — o nome da
   * credencial, por exemplo. Ficam dentro deste form para que a tela tenha UM Salvar,
   * em vez de um campo que grava sozinho ao perder o foco.
   */
  header?: React.ReactNode
  /** O `header` foi alterado: habilita o Salvar mesmo sem campo do metadata sujo. */
  headerDirty?: boolean
  /** Descartar também precisa reverter o que o `header` mudou. */
  onDiscard?: () => void
  // Rótulo do botão de submit (default: Salvar).
  submitLabel?: string
}

export const ProviderForm = ({
  fields: allFields,
  currentValues,
  mode,
  saving,
  onSubmit,
  footerStart,
  submitLabel,
  header,
  headerDirty = false,
  onDiscard,
}: ProviderFormProps) => {
  const { t } = useTranslation()

  // A referência ao cofre nunca entra neste form: na edição ela é escolhida na
  // aba Certificado e na criação tem passo próprio no stepper — sempre ao lado
  // do contexto que dá sentido à escolha. Deixá-la aqui também faria o submit
  // reenviar o valor da tela, sobrescrevendo com um valor velho a troca feita
  // no outro lugar.
  const fields = allFields.filter(
    (field) => !isCertificateRefField(field)
  )

  const buildInitialValues = () =>
    Object.fromEntries(
      fields.map((field) => [
        field.name,
        initialValue(field, currentValues),
      ])
    )

  const [values, setValues] = useState<
    Record<string, string | boolean>
  >(buildInitialValues)
  // Campos que o usuário tocou (payload parcial no edit).
  const [dirty, setDirty] = useState<Set<string>>(
    () => new Set()
  )
  // Write-only destravados pelo botão "alterar".
  const [unlocked, setUnlocked] = useState<Set<string>>(
    () => new Set()
  )
  const [errors, setErrors] = useState<
    Record<string, string>
  >({})

  const fieldLabel = (field: ProcessorMetadataField) =>
    t(`${panelPrefix}.providerFields.${field.name}`, {
      defaultValue: field.name,
    })

  const setFieldValue = (
    name: string,
    value: string | boolean
  ) => {
    setValues((prev) => ({ ...prev, [name]: value }))
    setDirty((prev) => new Set(prev).add(name))
    setErrors((prev) => {
      if (!prev[name]) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  const isDirty = dirty.size > 0 || headerDirty

  const handleDiscard = () => {
    setValues(buildInitialValues())
    setDirty(new Set())
    setUnlocked(new Set())
    setErrors({})
    onDiscard?.()
  }

  const handleSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    const payload: Record<string, unknown> = {}
    const nextErrors: Record<string, string> = {}

    fields.forEach((field) => {
      const raw = values[field.name]
      const writeOnly = isWriteOnlyField(field)

      if (writeOnly) {
        const typed =
          typeof raw === "string" ? raw.trim() : ""
        // No edit o write-only só entra se destravado ("Alterar") E
        // preenchido — ausente mantém o valor atual no backend
        // (update parcial). No create o input já nasce aberto.
        const editable =
          mode === "create" || unlocked.has(field.name)
        if (editable && typed) {
          payload[field.name] = typed
        } else if (mode === "create" && field.required) {
          nextErrors[field.name] = t(
            `${panelPrefix}.form.required`
          )
        }
        return
      }

      if (field.type === "BOOLEAN") {
        if (mode === "create" || dirty.has(field.name)) {
          payload[field.name] = Boolean(raw)
        }
        return
      }

      const text =
        typeof raw === "string" ? raw.trim() : String(raw)
      if (mode === "create") {
        if (!text && field.required) {
          nextErrors[field.name] = t(
            `${panelPrefix}.form.required`
          )
          return
        }
        if (text) payload[field.name] = text
        return
      }
      // Edit: só o que mudou (mascarado intocado fica de fora).
      if (dirty.has(field.name)) {
        if (!text && field.required) {
          nextErrors[field.name] = t(
            `${panelPrefix}.form.required`
          )
          return
        }
        payload[field.name] = text || null
      }
    })

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    onSubmit(payload)
  }

  const renderInput = (field: ProcessorMetadataField) => {
    const label = fieldLabel(field)
    const value = values[field.name]

    if (field.type === "BOOLEAN") {
      return (
        <Switch
          checked={Boolean(value)}
          aria-label={label}
          onCheckedChange={(next) =>
            setFieldValue(field.name, next)
          }
        />
      )
    }

    if (field.type === "ENUM") {
      return (
        <Select
          value={
            typeof value === "string" && value
              ? value
              : undefined
          }
          onValueChange={(next) =>
            setFieldValue(field.name, next)
          }
        >
          <SelectTrigger
            className="w-full"
            aria-label={label}
          >
            <SelectValue
              placeholder={t(
                `${panelPrefix}.form.selectPlaceholder`
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {/* Rotulo por opcao quando existir (ex.: processStep do Itau,
                    onde VALIDACAO significa "teste" e o valor de fio e derivado
                    por modalidade no backend); sem chave, mostra o valor cru. */}
                {t(
                  `${panelPrefix}.providerFieldOptions.${field.name}.${option}`,
                  { defaultValue: option }
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (field.type === "PEM_PRIVATE_KEY") {
      return (
        <Textarea
          rows={4}
          className="font-mono text-xs"
          aria-label={label}
          placeholder="-----BEGIN PRIVATE KEY-----"
          value={typeof value === "string" ? value : ""}
          onChange={(event) =>
            setFieldValue(field.name, event.target.value)
          }
        />
      )
    }

    if (field.type === "PEM_CERTIFICATE") {
      return (
        <Textarea
          rows={4}
          className="font-mono text-xs"
          aria-label={label}
          placeholder="-----BEGIN CERTIFICATE-----"
          value={typeof value === "string" ? value : ""}
          onChange={(event) =>
            setFieldValue(field.name, event.target.value)
          }
        />
      )
    }

    if (field.type === "SECRET") {
      return (
        <PasswordInput
          aria-label={label}
          autoComplete="new-password"
          value={typeof value === "string" ? value : ""}
          onChange={(event) =>
            setFieldValue(field.name, event.target.value)
          }
        />
      )
    }

    // STRING e tipos desconhecidos → input de texto.
    return (
      <Input
        aria-label={label}
        value={typeof value === "string" ? value : ""}
        onChange={(event) =>
          setFieldValue(field.name, event.target.value)
        }
      />
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      noValidate
    >
      {header}
      {fields.map((field) => {
        const label = fieldLabel(field)
        const writeOnly = isWriteOnlyField(field)
        // Read do write-only = indicador `<name>Defined`.
        const defined = Boolean(
          currentValues?.[`${field.name}Defined`]
        )
        const locked =
          writeOnly &&
          mode === "edit" &&
          !unlocked.has(field.name)

        return (
          <div
            key={field.name}
            className="space-y-1.5"
            data-testid={`provider-field-${field.name}`}
          >
            <Label className="text-sm">
              {label}
              {field.required && (
                <span className="ml-0.5 text-destructive">
                  *
                </span>
              )}
            </Label>
            {locked ? (
              // Write-only intocado: "•••• definido" + alterar.
              <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <span className="text-sm text-muted-foreground">
                  {defined
                    ? t(`${panelPrefix}.form.secretDefined`)
                    : t(
                        `${panelPrefix}.form.secretNotDefined`
                      )}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUnlocked((prev) =>
                      new Set(prev).add(field.name)
                    )
                  }
                >
                  {t(`${panelPrefix}.form.change`)}
                </Button>
              </div>
            ) : (
              renderInput(field)
            )}
            {errors[field.name] && (
              <p className="text-xs text-destructive">
                {errors[field.name]}
              </p>
            )}
          </div>
        )
      })}

      <div className="flex items-center justify-between gap-2 border-t pt-4">
        <div>{footerStart}</div>
        <div className="flex items-center gap-2">
          {mode === "edit" && isDirty && (
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={handleDiscard}
            >
              {t(`${panelPrefix}.form.discard`)}
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              saving || (mode === "edit" && !isDirty)
            }
          >
            {submitLabel ?? t(`${panelPrefix}.form.save`)}
          </Button>
        </div>
      </div>
    </form>
  )
}
